import axios from 'axios';
import cookie from 'react-cookies'
import service, { basicUrl } from './index'
import { RIMS_LOCAL_STG_CONSTANTS } from '../constants';
import { getClientLocation } from '../common/clientLocation';

const prefix = '/audit/log-record';

// ACL
export const fetchAuditList = (params: any) => service.post(`${prefix}/list`, params);

export const fetchAuditFieldModification = (auditId: string) => service.get(prefix + `/one/modification-info?auditId=${auditId}`);

export const fetchOperationListByEntityId = (entityId: string, fromService: string, pageNum: number, pageSize: number) => service.get(prefix + `/entity/operation-list?entityId=${entityId}&fromService=${fromService}&pageNum=${pageNum}&pageSize=${pageSize}`);

export const EXPORT_AUDIT_LOG_URL = `${prefix}/export`;

export const EXPORT_AUDIT_ENTITY_IDENTIFIER_URL = `${prefix}/export/entity/operation-list`

// UI-side audit for User Admin, same payload as the portal-urims-fe login audit
const AUDIT_FROM_SERVICE = 'UAM';
const USER_RECORD_ID_PREFIX = 'U-';
const USER_AUDIT_TIMEOUT_MS = 5000;
// Max time to wait for IP / country before sending the audit without them
const CLIENT_LOCATION_TIMEOUT_MS = 3000;

export const USER_AUDIT_OPERATION = {
  ADD: 'Add',
  EDIT: 'Edit',
  VIEW: 'View',
  EXPORT: 'Export',
};

export interface AuditFieldModification {
  fieldName: string,
  oldValue: string,
  newValue: string,
}

export interface UserAuditInfo {
  operationType: string,
  userId?: string | number,
  entityName: string,
  entityIdentifier?: string,
  fieldModifications?: AuditFieldModification[],
  entityCreatedByName?: string,
  entityCreatedDate?: string,
  entityModifiedByName?: string,
  entityModifiedDate?: string,
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAYLOAD_DATE_PATTERN = /^\d{2}-[A-Z][a-z]{2}-\d{4} \d{2}:\d{2}:\d{2}$/;

// Same payload date format as KPro: dd-MMM-yyyy HH:mm:ss
const formatPayloadDate = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}-${MONTHS[date.getMonth()]}-${date.getFullYear()} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// Dates loaded from the user API are display values, the audit API only accepts the payload format
const toPayloadDate = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  if (PAYLOAD_DATE_PATTERN.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? fallback : formatPayloadDate(parsed);
}

const getClientLocationWithTimeout = () => {
  const timeout = new Promise<{ ipAddress: string, country: string }>(resolve =>
    setTimeout(() => resolve({ ipAddress: '', country: '' }), CLIENT_LOCATION_TIMEOUT_MS));
  return Promise.race([getClientLocation(), timeout]);
}

// Portal logins share the user through cookies; USER_INFO exists in localStorage only after
// logging in on this app's own login page. Email is the fallback for sessions started before
// the portal began sharing USER_NAME.
const getOperatorUsername = () => {
  const userInfoJson = window.localStorage.getItem(RIMS_LOCAL_STG_CONSTANTS.USER_INFO);
  const userInfo: any = userInfoJson ? JSON.parse(userInfoJson) : null;
  return userInfo?.username
    || cookie.load(RIMS_LOCAL_STG_CONSTANTS.USER_NAME)
    || userInfo?.email
    || cookie.load(RIMS_LOCAL_STG_CONSTANTS.EMAIL)
    || '';
}

const postUserAudit = async (info: UserAuditInfo) => {
  const { ipAddress, country } = await getClientLocationWithTimeout();
  const operator = getOperatorUsername();
  const now = formatPayloadDate(new Date());

  const auditLog = {
    operatorUsername: `${operator} | IP: ${ipAddress}| Location: ${country}`,
    operationType: info.operationType,
    entityIdentifier: info.entityIdentifier || USER_RECORD_ID_PREFIX + (info.userId ?? ''),
    entityName: info.entityName,
    fromService: AUDIT_FROM_SERVICE,
    operationDate: now,
    entityCreatedByName: info.entityCreatedByName || operator,
    entityCreatedDate: toPayloadDate(info.entityCreatedDate, now),
    entityModifiedByName: info.entityModifiedByName || operator,
    entityModifiedDate: toPayloadDate(info.entityModifiedDate, now),
    fieldModifications: [
      ...(info.fieldModifications || []),
      { fieldName: 'Location', oldValue: '', newValue: country },
    ],
  };

  // Not through the shared service: its interceptor shows a server-error snackbar on 500,
  // which a background audit call must not do
  await axios.post(`${(basicUrl || '').replace(/\/$/, '')}/audit/system/audit-log`, auditLog, {
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
      Authorization: `Bearer ${cookie.load(RIMS_LOCAL_STG_CONSTANTS.LOGIN_TOKEN)}`,
    },
    withCredentials: true,
    timeout: USER_AUDIT_TIMEOUT_MS,
  });
}

/**
 * Save a User Admin audit record with client IP and location. Never throws, so callers
 * can fire it after a successful action without affecting the UI flow.
 */
export const saveUserAudit = (info: UserAuditInfo): Promise<void> => {
  return postUserAudit(info).catch((error: any) => {
    console.error('Failed to save user audit', error);
  });
}

/** Old/new pairs for the fields that actually changed */
export const diffAuditFields = (labels: Record<string, string>, oldData: any, newData: any): AuditFieldModification[] => {
  return Object.keys(labels)
    .map(key => ({
      fieldName: labels[key],
      oldValue: oldData?.[key] == null ? '' : String(oldData[key]),
      newValue: newData?.[key] == null ? '' : String(newData[key]),
    }))
    .filter(item => item.oldValue !== item.newValue);
}
